import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { PLAN_STATUSES, planStatusLabel } from './planConstants';

const emptyForm = {
  name: '',
  code: '',
  description: '',
  status: 'draft',
  monthly_price: '0',
  annual_price: '0',
  api_call_limit: '0',
  storage_limit_mb: '0',
  max_users: '0',
  max_api_keys: '0',
  trial_days: '0',
  sort_order: '0'
};

export default function PlatformPlanFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/plans/${id}?includeDeleted=1`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load plan');
      const p = json.plan;
      setForm({
        name: p.name || '',
        code: p.code || '',
        description: p.description || '',
        status: p.status || 'draft',
        monthly_price: String(p.monthly_price ?? 0),
        annual_price: String(p.annual_price ?? 0),
        api_call_limit: String(p.api_call_limit ?? 0),
        storage_limit_mb: String(p.storage_limit_mb ?? 0),
        max_users: String(p.max_users ?? 0),
        max_api_keys: String(p.max_api_keys ?? 0),
        trial_days: String(p.trial_days ?? 0),
        sort_order: String(p.sort_order ?? 0)
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (ready && user?.role !== 'super_admin') {
      navigate('/platform/plans', { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      name: form.name.trim(),
      code: form.code.trim().toLowerCase(),
      description: form.description.trim() || null,
      status: form.status,
      monthly_price: Number(form.monthly_price),
      annual_price: Number(form.annual_price),
      api_call_limit: Number(form.api_call_limit),
      storage_limit_mb: Number(form.storage_limit_mb),
      max_users: Number(form.max_users),
      max_api_keys: Number(form.max_api_keys),
      trial_days: Number(form.trial_days),
      sort_order: Number(form.sort_order)
    };
    if (isEdit) {
      delete body.code;
    }
    try {
      const res = await platformApiFetch(
        isEdit ? `/api/platform/plans/${id}` : '/api/platform/plans',
        {
          method: isEdit ? 'PATCH' : 'POST',
          body
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      navigate(`/platform/plans/${json.plan.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready || user?.role !== 'super_admin') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit plan' : 'Create plan'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 720 }}>
        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Code"
          value={form.code}
          onChange={(e) => set('code', e.target.value)}
          fullWidth
          required={!isEdit}
          disabled={isEdit}
          margin="normal"
          helperText={isEdit ? 'Code cannot be changed' : 'Lowercase letters, numbers, _ and -'}
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          fullWidth
          margin="normal"
          multiline
          minRows={3}
        />
        <TextField
          select
          label="Status"
          value={form.status}
          onChange={(e) => set('status', e.target.value)}
          fullWidth
          margin="normal"
        >
          {PLAN_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {planStatusLabel(s)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Monthly price"
          type="number"
          inputProps={{ step: '0.01', min: 0 }}
          value={form.monthly_price}
          onChange={(e) => set('monthly_price', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Annual price"
          type="number"
          inputProps={{ step: '0.01', min: 0 }}
          value={form.annual_price}
          onChange={(e) => set('annual_price', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="API call limit"
          type="number"
          inputProps={{ min: 0 }}
          value={form.api_call_limit}
          onChange={(e) => set('api_call_limit', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Storage limit (MB)"
          type="number"
          inputProps={{ min: 0 }}
          value={form.storage_limit_mb}
          onChange={(e) => set('storage_limit_mb', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Max users"
          type="number"
          inputProps={{ min: 0 }}
          value={form.max_users}
          onChange={(e) => set('max_users', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Max API keys"
          type="number"
          inputProps={{ min: 0 }}
          value={form.max_api_keys}
          onChange={(e) => set('max_api_keys', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Trial days"
          type="number"
          inputProps={{ min: 0 }}
          value={form.trial_days}
          onChange={(e) => set('trial_days', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Sort order"
          type="number"
          value={form.sort_order}
          onChange={(e) => set('sort_order', e.target.value)}
          fullWidth
          margin="normal"
          helperText="Lower numbers appear first in tenant catalog"
        />
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button type="button" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
